import { useSelector } from 'react-redux';

import {
    AppState, LeftButtons, useSelectorCurrentProductId, useSelectorIsDefaultProduct
} from '../app';
import { ProductLogoNames } from '../app/components/product-logo';
import { hasProductLicenceAccess } from './utils/licence-utils';
import {
    Module, ModuleTypes, ProductLicence, School, UserTypes
} from '@api';
import { TeacherAdminIcons } from '@components';

export const useSelectorUserProfiles = () => {
    return useSelector<AppState, AppState['userProfiles']>(
        state => state.userProfiles
    );
};

export const useModuleByIdSelector = (id: number) => {
    return useSelector<AppState, Module | null>(
        state => state.userProfiles?.modules?.byId?.[id] || null
    );
};

export const useGroupByIdSelector = (id: string) => {
    return useSelector<AppState, Array<number> | null>(
        state => state.userProfiles?.groups?.byId?.[id] || null
    );
};

export const useSelectedModuleArrayById = (selectedModuleIds: Array<number>) => {
    return useSelector<AppState, Array<Module>>(state => {
        const currentModules = state.userProfiles?.modules?.byId || {};

        return selectedModuleIds.map((id) => currentModules[id]);
    });
};

export const useSelectorSchool = () => {
    return useSelector<AppState, School | null>(
        (state) => state.userProfiles.data?.school || null
    );
};
/**
 * Select the product license for the given product id.
 * This respects the behaviour in Angular portal to only return an icon when the
 * product selector is being used by checking the default product id and the matching one.
 *
 * @see https://bitbucket.org/mathletics/3p-portal/src/c0f90eb0c2e629c170e29e3bbd482fc3db0cd3ea/common/directive/product-selector/product-selector.js#product-selector.js-77
 * @see https://bitbucket.org/mathletics/3p-portal/src/c0f90eb0c2e629c170e29e3bbd482fc3db0cd3ea/view/header/header.js#header.js-51
 */

export function useSelectorProductLicenceIcon (productId?: number) {
    const isDefaultProduct = useSelectorIsDefaultProduct();

    return useSelector<AppState, ProductLogoNames | null>(state => {
        if (typeof productId !== 'number') {
            return null;
        }

        const product = state.userProfiles.data?.productLicenceContainer?.productLicences?.find(
            product => product.id === productId
        );

        return !isDefaultProduct && product?.productIcon as ProductLogoNames || null;
    });
}

export function selectProductLicences (state: AppState): Array<ProductLicence> {
    return state.userProfiles.data?.productLicenceContainer?.productLicences || [];
}

export function useSelectProductLicences () {
    return useSelector(selectProductLicences);
}

export function selectProductLicence (state: AppState, productId: number) {
    return selectProductLicences(state).find(
        licence => licence.id === productId
    ) || null;
}

export function selectHasProductAccess (
    state: AppState,
    defaultProductId: number | null
): boolean {
    const productLicences = selectProductLicences(state);
    const productLicenceSwitchingTo = defaultProductId && selectProductLicence(state, defaultProductId) || null;
    const hasAccess = hasProductLicenceAccess({
        licence: productLicenceSwitchingTo,
        licences: productLicences,
        userProfile: state.userProfiles.data?.user || null
    });

    return hasAccess;
}

export function useSelectorHasCurrentProductAccess () {
    const currentProductId = useSelectorCurrentProductId();

    return useSelector<AppState, boolean>(
        (state) => selectHasProductAccess(state, currentProductId)
    );
}

export function useSelectorIsProfileLoaded () {
    return useSelector<AppState, boolean>(
        state => state?.userProfiles?.isSuccess
    );
}

export function useSelectorIsInternalUsers (): boolean {
    const userType = useSelector<AppState, null | number>(state => state.userProfiles.data?.user?.userProfileType || null);

    return userType === UserTypes.internalUsers;
}

export function useSelectorIsTeacherUser (): boolean {
    const userType = useSelector<AppState, null | number>(state => state.userProfiles.data?.user?.userProfileType || null);

    return userType === UserTypes.teachers;
}

export function useSelectorIsAdminUser (): boolean {
    const userType = useSelector<AppState, null | number>(state => state.userProfiles.data?.user?.userProfileType || null);

    return userType === UserTypes.schoolAdmins;
}

export function useSelectorLeftNavButtons (): Array<LeftButtons> {
    const currentProductId = useSelectorCurrentProductId();
    const isInternalUsers = useSelectorIsInternalUsers();
    const {
        groups, modules
    } = useSelectorUserProfiles();

    if (!groups || !modules) {
        return [];
    }

    return groups.allIds.map(groupId => {
        // Get the array of module ids
        let selectedGroupIds = groups.byId[groupId];

        // Filter the array of module ids by product selector. We do not need to do this if Internal User
        if (!isInternalUsers) {
            selectedGroupIds = selectedGroupIds.filter((id) => {
                const tempModule = modules.byId[id];

                return tempModule.productIds.includes(currentProductId);
            });
        }

        // Filter the active modules from the list of all moduleIds in the selectedGroupIds
        // Modules are active if they are 'not hidden' and 'not disabled'
        const activeModulesInSelectedGroup = selectedGroupIds.filter(moduleId => {
            const module = modules.byId[moduleId];

            return !module.isHidden && !module.disabled;
        });
        const moduleType = selectedGroupIds.length > 1 && activeModulesInSelectedGroup.length > 1 ?
            ModuleTypes.landing : ModuleTypes.content;
        const isValidGroup = activeModulesInSelectedGroup.length > 0;
        // Get initial module - modules are invalid if there are no active modules (not hidden, not disabled) in the selected groupId
        const initialModule = isValidGroup ? modules.byId[activeModulesInSelectedGroup[0]] : null;

        return {
            moduleType,
            icon: initialModule?.icon as TeacherAdminIcons,
            moduleId: initialModule?.id,
            groupId: initialModule?.group,
            text: groupId || initialModule?.name,
            tooltip: initialModule?.tooltip,
            moduleIds: selectedGroupIds,
            moduleLink: initialModule?.moduleLink,
            isAvailableForOneRosterSchoolOnly: initialModule?.isAvailableForOneRosterSchoolOnly,
            isHiddenForOneRosterSchool: initialModule?.isHiddenForOneRosterSchool
        };
    });
}
